/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import './GoogleMap.less';
import ICON_PATH from '../../../assets/images/custom-marker.png';
import ICON_PATH_A2 from '../../../assets/images/custom-marker-2.png';

import { sendUserMovesPin } from '../edap';
import { logPinMapMovement } from '../../../utils/EventProviders/EgClickstreamUtil';
import log from '../../../utils/ClientLogger';
// import { PIN_DELTA } from '../../../constants/common';
import useDidMountEffect from '../../../utils/hooks/useDidMountEffect';

const MAP_STYLES = {
    v1: [],
    v2: [
        {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [
                {
                    color: '#7C8F9B'
                }
            ]
        },
        {
            featureType: 'poi',
            elementType: 'labels.text.stroke',
            stylers: [
                { color: '#FFFFFF' }
            ]
        },
        {
            featureType: 'trans`it',
            stylers: [
                { visibility: 'off' }
            ]
        }
    ]
};

const PIN_SIZE = {
    v1: {
        x: 34,
        y: 40
    },
    v2: {
        x: 56,
        y: 56
    }
};
export const getNewPosition = (markerLat, markerLng, bounds) => {
    const newPosition = {
        lat: markerLat,
        lng: markerLng
    };
    if (newPosition.lat > bounds.maxLat) newPosition.lat = bounds.maxLat;
    if (newPosition.lng > bounds.maxLng) newPosition.lng = bounds.maxLng;
    if (newPosition.lat < bounds.minLat) newPosition.lat = bounds.minLat;
    if (newPosition.lng < bounds.minLng) newPosition.lng = bounds.minLng;

    return newPosition;
};

export function MovableMap(props) {
    const {
        createGoogleMapViewTest,
        mapsApi,
        lat,
        lng,
        onPositionChange,
        text,
        error,
        trigger,
        address,
        pageName,
        brand,
        display,
        isAddressV2,
        tooltip,
        onPinMove
    } = props;

    if (!display) return null;

    const [animated, setAnimated] = useState(false);
    const googleMapView = useRef();
    const googleMap = useRef();
    const marker = useRef();
    const [position, setPosition] = useState({});
    const [hasMoved, setHasMoved] = useState(false);

    const updatedLat = useRef(lat);
    const updatedLng = useRef(lng);

    const savePosition = (payload) => {
        const latLng = {
            lat: payload.lat(),
            lng: payload.lng()
        };
        sendUserMovesPin(trigger);
        trigger('interaction', logPinMapMovement(address, pageName, brand));
        log('info', 'Map Pin moved', {
            address,
            latLng
        });
        setPosition(latLng);
    };

    const pinSizeToUse = isAddressV2 ? PIN_SIZE.v2 : PIN_SIZE.v1;

    const mapPinCenter = () => ({
        // eslint-disable-next-line no-undef
        anchor: new google.maps.Point(pinSizeToUse.x / 2, pinSizeToUse.y),
        url: isAddressV2 ? ICON_PATH_A2 : ICON_PATH,
        // eslint-disable-next-line no-undef, max-len
        scaledSize: new google.maps.Size(pinSizeToUse.x, pinSizeToUse.y)// scaled size
    });

    const mapStyleToUse = isAddressV2 ? MAP_STYLES.v2 : MAP_STYLES.v1;

    const createGoogleMapView = () => {
        if (createGoogleMapViewTest) createGoogleMapViewTest(marker.current, googleMap.current);
        if (!mapsApi || Object.keys(mapsApi).length === 0) return;
        setAnimated(true);
        try {
            googleMap.current = new mapsApi.Map(googleMapView.current, {
                zoom: 17,
                center: position,
                zoomControl: true,
                zoomControlOptions: {
                    position: mapsApi.ControlPosition.RIGHT_TOP
                },
                disableDefaultUI: true,
                styles: mapStyleToUse,
                options: {
                    maxZoom: 20,
                    streetViewControl: false,
                    mapTypeControl: false,
                    clickableIcons: false,
                    keyboardShortcuts: false
                }
            });
            marker.current = new mapsApi.Marker({
                animation: mapsApi.Animation ? mapsApi.Animation.DROP : undefined,
                map: googleMap.current,
                draggable: true,
                position,
                icon: mapPinCenter(),
                crossOnPinChange: false
            });

            window.google.maps.event.addListener(marker.current, 'dragend', () => {
                onPinMove();
                savePosition(marker.current.getPosition());
            });

            if (createGoogleMapViewTest) createGoogleMapViewTest(marker.current, googleMap.current);
        // eslint-disable-next-line no-empty
        } catch (e) { }
    };

    useDidMountEffect(() => {
        const isLocationSet = lat && lng;
        updatedLat.current = lat;
        updatedLng.current = lng;
        if (isLocationSet) {
            setAnimated(true);
            setPosition({
                lat,
                lng
            });
        }
    }, [lat, mapsApi, lng]);

    useDidMountEffect(() => {
        if (googleMap.current) {
            googleMap.current.setCenter(position);
            marker.current.setPosition(position);
        } else {
            createGoogleMapView();
        }
        onPositionChange(position);
    }, [position]);

    useEffect(() => {
        if (animated) {
            setTimeout(() => { setAnimated(false); }, 1000);
        }
    }, [animated]);

    const isLocationSet = lat && lng;
    const classes = classnames(
        'google-map-view',
        'map-container',
        { 'big-map': isAddressV2 },
        { 'no-display': !isLocationSet, animated }
    );

    const handleMapClick = () => {
        setHasMoved(true);
    };

    return (
        <section className={classnames('map-section', 'location', { hide: !isLocationSet })}>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div data-wdio="google-map-component" className={classes} ref={googleMapView} onClick={handleMapClick} />
            { isAddressV2 && <p className={classnames('map-tooltip', { fade: hasMoved })}>{tooltip}</p>}
            {
                !isAddressV2 && (
                    error
                        ? (
                            <div className={classnames('fds-field', 'invalid')}>
                                <span className="fds-validation-message" role="alert">
                                    <span className="fds-icon fds-icon-name-error"><svg role="img" className="fds-icon-svg" aria-hidden="true"><use xlinkHref="#error" /></svg></span>
                                    {error}
                                </span>
                            </div>
                        )
                        : <span className={classnames('map-footer')}>{text}</span>
                )
            }

        </section>
    );
}

MovableMap.propTypes = {
    createGoogleMapViewTest: PropTypes.func,
    lat: PropTypes.number,
    lng: PropTypes.number,
    // eslint-disable-next-line react/forbid-prop-types
    mapsApi: PropTypes.object,
    onPositionChange: PropTypes.func.isRequired,
    onPinMove: PropTypes.func,
    text: PropTypes.string.isRequired,
    error: PropTypes.string,
    trigger: PropTypes.func.isRequired,
    address: PropTypes.shape().isRequired,
    pageName: PropTypes.string.isRequired,
    brand: PropTypes.string.isRequired,
    display: PropTypes.bool.isRequired,
    isAddressV2: PropTypes.bool.isRequired,
    tooltip: PropTypes.string.isRequired
};

MovableMap.defaultProps = {
    createGoogleMapViewTest: () => {},
    lat: null,
    lng: null,
    mapsApi: {},
    error: '',
    onPinMove: () => {}
};

export default MovableMap;
